/**
 * Datos academicos MINIMOS necesarios para cobros.
 *
 * La PWA no administra el sistema academico: guarda solo la copia autorizada que
 * necesita para cobrar. Todo lo que no aparece aqui, no se descarga al dispositivo.
 */

import { z } from 'zod';
import { idSchema, instantSchema, moneySchema, operationalDateSchema } from './common.js';

export const institutionSchema = z.object({
  id: idSchema,
  name: z.string(),
  legalName: z.string().nullable(),
  taxId: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  timeZone: z.string(),
  currency: z.string(),
  /** Si permite dejar excedente como saldo a favor. */
  allowOverpayment: z.boolean(),
  logoDataUrl: z.string().nullable(),
  version: z.number().int(),
});
export type InstitutionDto = z.infer<typeof institutionSchema>;

export const cycleSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  name: z.string(),
  startDate: operationalDateSchema,
  endDate: operationalDateSchema,
  closed: z.boolean(),
  isCurrent: z.boolean(),
  version: z.number().int(),
});
export type CycleDto = z.infer<typeof cycleSchema>;

export const gradeSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  name: z.string(),
  orderIndex: z.number().int(),
  version: z.number().int(),
});
export type GradeDto = z.infer<typeof gradeSchema>;

export const sectionSchema = z.object({
  id: idSchema,
  gradeId: idSchema,
  name: z.string(),
  version: z.number().int(),
});
export type SectionDto = z.infer<typeof sectionSchema>;

export const guardianSchema = z.object({
  fullName: z.string(),
  phone: z.string().nullable(),
  relationship: z.string().nullable(),
});

export const studentSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  /** Identificador interno visible (carnet). Unico dentro de la institucion. */
  code: z.string(),
  fullName: z.string(),
  active: z.boolean(),
  cycleId: idSchema,
  gradeId: idSchema,
  gradeName: z.string(),
  sectionId: idSchema,
  sectionName: z.string(),
  /** Solo si el contrato de la API lo permite; puede venir vacio. */
  guardian: guardianSchema.nullable(),
  /** Proyeccion de saldo. La fuente de verdad es la reconstruccion desde cargos. */
  outstanding: moneySchema,
  credit: moneySchema,
  version: z.number().int(),
  updatedAt: instantSchema,
  deletedAt: instantSchema.nullable(),
});
export type StudentDto = z.infer<typeof studentSchema>;

export const conceptSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  code: z.string(),
  name: z.string(),
  kind: z.enum(['INSCRIPCION', 'COLEGIATURA', 'MORA', 'DESCUENTO', 'BECA', 'AJUSTE', 'OTRO']),
  active: z.boolean(),
  defaultAmount: moneySchema.nullable(),
  version: z.number().int(),
});
export type ConceptDto = z.infer<typeof conceptSchema>;

export const chargeSchema = z.object({
  id: idSchema,
  studentId: idSchema,
  cycleId: idSchema,
  conceptId: idSchema,
  conceptName: z.string(),
  conceptActive: z.boolean(),
  amount: moneySchema,
  applied: moneySchema,
  outstanding: moneySchema,
  dueDate: operationalDateSchema.nullable(),
  voided: z.boolean(),
  version: z.number().int(),
});
export type ChargeDto = z.infer<typeof chargeSchema>;

export const studentSearchQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  cycleId: idSchema.optional(),
  gradeId: idSchema.optional(),
  sectionId: idSchema.optional(),
  /** Filtro por estado de pago derivado del saldo. */
  paymentState: z.enum(['TODOS', 'AL_DIA', 'CON_SALDO', 'CON_CREDITO']).default('TODOS'),
  active: z.enum(['TODOS', 'ACTIVOS', 'INACTIVOS']).default('ACTIVOS'),
});
export type StudentSearchQuery = z.infer<typeof studentSearchQuerySchema>;

export const studentDetailSchema = z.object({
  student: studentSchema,
  charges: z.array(chargeSchema),
  /** Fecha de corte de esta consulta. La PWA la muestra junto al saldo. */
  asOf: instantSchema,
});
export type StudentDetailDto = z.infer<typeof studentDetailSchema>;
